import React, { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_CONSULT_ALIASES,
  DEFAULT_PROCEDURE_ALIASES,
  computeReferralsTab,
  formatDays,
  loadStoredAliases,
  parseAliasInput,
  saveStoredAliases,
} from './overviewReferralsCompute';

function FunnelBar({ label, count, pctOfRecv, color, widthPct }) {
  return (
    <div className="overview-funnel-row">
      <div className="overview-funnel-meta">
        <span>{label}</span>
        <b>
          {count.toLocaleString()}
          {pctOfRecv != null && (
            <span className="overview-funnel-pct"> · {Math.round(pctOfRecv * 100)}%</span>
          )}
        </b>
      </div>
      <div className="overview-funnel-track">
        <i style={{ width: `${Math.max(widthPct, count > 0 ? 2 : 0)}%`, background: color }} />
      </div>
    </div>
  );
}

function OverviewReferralsTab({
  referralRecords,
  priorReferralRecords,
  eventRecords,
  facIds,
  windowLabel,
  loading,
}) {
  const stored = loadStoredAliases();
  const [includeInternal, setIncludeInternal] = useState(false);
  const [consultText, setConsultText] = useState(
    (stored?.consult || DEFAULT_CONSULT_ALIASES).join(', ')
  );
  const [procedureText, setProcedureText] = useState(
    (stored?.procedure || DEFAULT_PROCEDURE_ALIASES).join(', ')
  );
  const [appliedConsult, setAppliedConsult] = useState(
    stored?.consult || DEFAULT_CONSULT_ALIASES
  );
  const [appliedProcedure, setAppliedProcedure] = useState(
    stored?.procedure || DEFAULT_PROCEDURE_ALIASES
  );

  useEffect(() => {
    saveStoredAliases(appliedConsult, appliedProcedure);
  }, [appliedConsult, appliedProcedure]);

  const stats = useMemo(
    () => computeReferralsTab({
      referralRecords,
      priorReferralRecords,
      eventRecords,
      facIds,
      includeInternal,
      consultAliases: appliedConsult,
      procedureAliases: appliedProcedure,
    }),
    [
      referralRecords,
      priorReferralRecords,
      eventRecords,
      facIds,
      includeInternal,
      appliedConsult,
      appliedProcedure,
    ]
  );

  const applyAliases = () => {
    setAppliedConsult(parseAliasInput(consultText));
    setAppliedProcedure(parseAliasInput(procedureText));
  };

  const mixTotal = stats.received || 1;
  const mixRows = [
    { key: 'direct', label: 'Direct in VitalEngine', color: '#1e3a8a', count: stats.mix.direct },
    { key: 'quick', label: 'Quick Refer link', color: '#0c5a64', count: stats.mix.quick },
    { key: 'external', label: 'External / non-VE sender', color: '#10b981', count: stats.mix.external },
  ];

  const speedDelta = stats.consultSpeedDelta;
  let speedDeltaEl = <span className="overview-delta flat">→ no prior-year compare</span>;
  if (speedDelta != null) {
    const abs = Math.abs(speedDelta).toFixed(1);
    if (Math.abs(speedDelta) < 0.05) {
      speedDeltaEl = <span className="overview-delta flat">→ no change vs LY</span>;
    } else if (speedDelta < 0) {
      speedDeltaEl = <span className="overview-delta up">▲ {abs} days faster vs LY</span>;
    } else {
      speedDeltaEl = <span className="overview-delta down">▼ {abs} days slower vs LY</span>;
    }
  }

  if (loading) {
    return <div className="overview-loading">Loading referrals…</div>;
  }

  return (
    <div className="overview-panel">
      <div className="overview-split">
        <div className="overview-box">
          <h3>
            <span>Referral to procedure · {windowLabel}</span>
            <span className="overview-toggle" role="group" aria-label="Internal referrals">
              Internal
              <button
                type="button"
                className={includeInternal ? 'off' : ''}
                onClick={() => setIncludeInternal(false)}
              >
                Excluded
              </button>
              <button
                type="button"
                className={includeInternal ? '' : 'off'}
                onClick={() => setIncludeInternal(true)}
              >
                Included
              </button>
            </span>
          </h3>

          <FunnelBar
            label="Received"
            count={stats.received}
            pctOfRecv={null}
            color="#1e3a8a"
            widthPct={100}
          />
          <FunnelBar
            label="Seen in clinic (consult)"
            count={stats.consults}
            pctOfRecv={stats.consultRate}
            color="#0c5a64"
            widthPct={stats.consultRate * 100}
          />
          <FunnelBar
            label="Procedure done"
            count={stats.procedures}
            pctOfRecv={stats.procedureRate}
            color="#10b981"
            widthPct={stats.procedureRate * 100}
          />

          <p className="overview-footnote">
            Consult / procedure counts come from Schedule Events matching the aliases below
            (same rules as Agentic Reporting → Referral Conversion).
            {' '}
            {includeInternal
              ? `Including ${stats.internalCount.toLocaleString()} internal (self) referral${stats.internalCount === 1 ? '' : 's'}.`
              : `Excluding ${stats.internalCount.toLocaleString()} internal (creating = receiving facility).`}
            {!stats.hasEvents && (
              <>
                {' '}
                <strong>No Schedule Events in cache</strong> — refresh “Referral Schedule Events”
                under Data Sources, or conversion stays at 0%.
              </>
            )}
          </p>

          <div className="overview-alias-box">
            <label>
              Consult event aliases
              <input
                type="text"
                value={consultText}
                onChange={e => setConsultText(e.target.value)}
                placeholder="Consult, Initial Consult, Clinic Visit"
              />
            </label>
            <label>
              Procedure event aliases
              <input
                type="text"
                value={procedureText}
                onChange={e => setProcedureText(e.target.value)}
                placeholder="Procedure, Case, OR Date"
              />
            </label>
            <button type="button" className="overview-btn" onClick={applyAliases}>
              Apply aliases
            </button>
          </div>
        </div>

        <div className="overview-panel" style={{ gap: 14 }}>
          <div className="overview-box">
            <h3>How referrals arrive</h3>
            <div className="overview-mix-bar" aria-hidden="true">
              {mixRows.map(row => (
                <i
                  key={row.key}
                  style={{
                    flex: Math.max(row.count, 0.001),
                    background: row.color,
                    display: row.count === 0 && stats.received === 0 ? 'none' : undefined,
                  }}
                  title={`${row.label}: ${row.count}`}
                />
              ))}
            </div>
            {mixRows.map(row => (
              <div key={row.key} className="overview-mix-row">
                <span className="overview-sw" style={{ background: row.color }} />
                <span className="grow">{row.label}</span>
                <b>{row.count.toLocaleString()}</b>
                <span className="overview-funnel-pct">
                  {Math.round((row.count / mixTotal) * 100)}%
                </span>
              </div>
            ))}
            <p className="overview-footnote">
              Quick Refer = referral_type 3. External = is_referred_by_non_vital_user.
              Direct = everything else. Not a dedicated “vFax converted” flag.
            </p>
          </div>

          <div className="overview-box">
            <h3>Speed</h3>
            <div className="overview-speed-grid">
              <div className="overview-kpi">
                <small>REFERRAL TO CONSULT</small>
                <b>{formatDays(stats.avgDaysToConsult)}</b>
                {speedDeltaEl}
                {stats.medianDaysToConsult != null && (
                  <span className="overview-footnote" style={{ marginTop: 4, display: 'block' }}>
                    Median {formatDays(stats.medianDaysToConsult)}
                  </span>
                )}
              </div>
              <div className="overview-kpi">
                <small>CONSULT TO PROCEDURE</small>
                <b>{formatDays(stats.avgDaysConsultToProc)}</b>
                <span className="overview-delta flat">avg among conversions</span>
                {stats.medianDaysConsultToProc != null && (
                  <span className="overview-footnote" style={{ marginTop: 4, display: 'block' }}>
                    Median {formatDays(stats.medianDaysConsultToProc)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OverviewReferralsTab;
